import { useState, useRef, useEffect } from "react";
import { http } from "@/lib/api";
import { Languages, Loader2, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";

/**
 * <TransliterateInput /> — a controlled <textarea> that lets a user type
 * in Latin script phonetically and see the target Indian script inserted
 * in-place when they hit ⏎ Space or click the "Convert" button.
 *
 * Backed by POST /api/i18n/transliterate. Adds a language chip to the
 * bottom-right of the textarea so the user can switch script anytime
 * without re-typing.
 *
 * Usage:
 *   const [body, setBody] = useState("");
 *   <TransliterateInput
 *     value={body}
 *     onChange={setBody}
 *     placeholder="Write in your language…"
 *     defaultLang="ml"
 *     data-testid="comment-body"
 *   />
 *
 * Behaviour:
 *   • ⏎ Space  → convert last-word only (fast, phone-style IME)
 *   • Cmd/Ctrl+Enter → convert full text
 *   • Click the language chip to switch or turn off
 */

const SUPPORTED = [
  { code: "off", name: "English (no convert)", native: "English", script: "Latin" },
  { code: "hi",  name: "Hindi",       native: "हिन्दी",     script: "Devanagari" },
  { code: "ml",  name: "Malayalam",   native: "മലയാളം",   script: "Malayalam" },
  { code: "ta",  name: "Tamil",       native: "தமிழ்",     script: "Tamil" },
  { code: "te",  name: "Telugu",      native: "తెలుగు",    script: "Telugu" },
  { code: "kn",  name: "Kannada",     native: "ಕನ್ನಡ",     script: "Kannada" },
  { code: "bn",  name: "Bengali",     native: "বাংলা",     script: "Bengali" },
  { code: "mr",  name: "Marathi",     native: "मराठी",     script: "Devanagari" },
  { code: "gu",  name: "Gujarati",    native: "ગુજરાતી",   script: "Gujarati" },
  { code: "pa",  name: "Punjabi",     native: "ਪੰਜਾਬੀ",     script: "Gurmukhi" },
  { code: "or",  name: "Odia",        native: "ଓଡ଼ିଆ",     script: "Odia" },
  { code: "as",  name: "Assamese",    native: "অসমীয়া",   script: "Bengali" },
  { code: "ur",  name: "Urdu",        native: "اردو",       script: "Perso-Arabic" },
];

const PREF_KEY = "sp:input-lang";

export default function TransliterateInput({
  value,
  onChange,
  placeholder,
  defaultLang,
  className,
  rows = 4,
  "data-testid": testId = "translit-input",
  ...rest
}) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return defaultLang || "off";
    return localStorage.getItem(PREF_KEY) || defaultLang || "off";
  });
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const taRef = useRef(null);

  const currentMeta = SUPPORTED.find((s) => s.code === lang) || SUPPORTED[0];

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(PREF_KEY, lang);
  }, [lang]);

  const doTransliterate = async (input) => {
    if (!input || !input.trim() || lang === "off") return input;
    try {
      const { data } = await http.post("/i18n/transliterate", {
        text: input,
        target_lang: lang,
      });
      return data.transliterated;
    } catch (e) {
      toast.error("Transliteration paused. Keep typing — will retry.");
      return input;
    }
  };

  // Convert the entire current value.
  const convertAll = async () => {
    if (!value || !value.trim() || lang === "off") return;
    setBusy(true);
    const converted = await doTransliterate(value);
    onChange(converted);
    setBusy(false);
  };

  // Convert only the last word before the cursor. Fires on ⏎ Space.
  const convertLastWord = async () => {
    if (lang === "off" || !value) return;
    const ta = taRef.current;
    const pos = ta ? ta.selectionStart : value.length;
    // Look backwards for the last whitespace boundary.
    const upto = value.slice(0, pos);
    const match = upto.match(/(\S+)\s*$/);
    if (!match) return;
    const word = match[1];
    // Skip if the word is already in native script (has any non-ASCII char).
    if (/[^\x00-\x7F]/.test(word)) return;
    setBusy(true);
    const converted = await doTransliterate(word);
    setBusy(false);
    if (!converted || converted === word) return;
    const before = value.slice(0, pos - word.length);
    const after = value.slice(pos);
    const newValue = before + converted + after;
    onChange(newValue);
    // Restore cursor after the converted word.
    setTimeout(() => {
      if (taRef.current) {
        const newPos = before.length + converted.length;
        taRef.current.selectionStart = taRef.current.selectionEnd = newPos + after.length - after.length;
      }
    }, 0);
  };

  const onKeyDown = async (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      await convertAll();
      return;
    }
    // Regular Space → convert the just-typed word (phone-IME style).
    if (e.key === " " && lang !== "off") {
      // Do NOT preventDefault — let the space go in, then convert.
      setTimeout(() => { convertLastWord(); }, 0);
    }
  };

  return (
    <div className={`relative ${className || ""}`} data-testid={`${testId}-wrap`}>
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || (lang !== "off" ? `Type in Latin — auto-converts to ${currentMeta.native}` : "Type here…")}
        rows={rows}
        className="w-full rounded-lg border px-3 py-2 text-sm bg-white"
        style={{ borderColor: "var(--wofy-border, #d1d5db)", minHeight: 96 }}
        data-testid={testId}
        {...rest}
      />
      <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
        {busy && <Loader2 className="w-3 h-3 animate-spin opacity-60" data-testid={`${testId}-busy`} />}
        <div className="relative">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setPickerOpen((v) => !v); }}
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-white shadow-sm hover:bg-black/5"
            style={{ borderColor: "var(--wofy-border, #d1d5db)", color: lang === "off" ? "#6B7280" : "#B58936" }}
            data-testid={`${testId}-lang-chip`}
            title={lang === "off" ? "Type in English (no conversion)" : `Auto-converting to ${currentMeta.native}. Click to change.`}
          >
            <Languages className="w-3 h-3" />
            {currentMeta.code === "off" ? "EN" : currentMeta.code.toUpperCase()}
            <ChevronDown className="w-2.5 h-2.5" />
          </button>
          {pickerOpen && (
            <div
              className="absolute bottom-full right-0 mb-1 rounded-lg border bg-white shadow-lg py-1 max-h-64 overflow-auto z-30"
              style={{ borderColor: "var(--wofy-border, #d1d5db)", minWidth: 200 }}
              data-testid={`${testId}-picker`}
            >
              {SUPPORTED.map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => { setLang(s.code); setPickerOpen(false); }}
                  className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 ${lang === s.code ? "font-semibold" : ""}`}
                  data-testid={`${testId}-picker-${s.code}`}
                >
                  <span className="opacity-60 mr-1.5 text-[10px] uppercase tracking-widest">{s.code}</span>
                  <span className="mr-1.5">{s.native}</span>
                  <span className="opacity-40 text-[10px]">· {s.script}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {lang !== "off" && (
        <div className="mt-1 text-[10px] opacity-60 flex items-center gap-2">
          <span>Type phonetically → hits <kbd className="px-1 border rounded text-[9px]">Space</kbd> to convert the last word</span>
          <button
            type="button"
            onClick={convertAll}
            className="underline hover:opacity-100"
            data-testid={`${testId}-convert-all`}
          >
            Convert all
          </button>
          <span>·</span>
          <button
            type="button"
            onClick={() => setLang("off")}
            className="underline hover:opacity-100 inline-flex items-center gap-0.5"
            data-testid={`${testId}-turn-off`}
          >
            <X className="w-2.5 h-2.5" /> Turn off
          </button>
        </div>
      )}
    </div>
  );
}
